"use client";

import { Authenticator, useAuthenticator } from "@aws-amplify/ui-react";
import { Amplify } from "aws-amplify";
import { FetchUserAttributesOutput, signOut } from "aws-amplify/auth";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

import { config as awsconfig } from "@/utils/amplifyServerUtils";

import AuthForm from "../AuthForm";
import AuthStateChangeDetector from "../AuthStateChangeDetector";

import Modal from "@/components/library/utils/Modal";
import { Image as ImageType } from "@/types/Image";
import "./assets/amplify-ui-react-styles.scss";
import "./index.scss";

Amplify.configure(awsconfig, { ssr: true });

const AuthButton = ({
  showAuthenticator,
  userAttributes,
  onClick,
}: {
  showAuthenticator: () => void;
  userAttributes: FetchUserAttributesOutput | null;
  onClick?: () => void;
}) => {
  const firstName = userAttributes?.given_name;

  const { authStatus } = useAuthenticator((context) => [context.authStatus]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    setDropdownOpen(false);
  }, [authStatus]);

  const _signOut = () => {
    if (signOut) {
      void signOut();
    }
  };

  useEffect(() => {
    const closeDropdown = (e: any) => {
      if (dropdownOpen && !e.target?.closest(".user-button")) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("click", closeDropdown);
    return () => document.removeEventListener("click", closeDropdown);
  }, [dropdownOpen]);

  if (authStatus === "authenticated" || firstName) {
    return (
      <div
        className={`component auth-button ${
          dropdownOpen ? "dropdown-opened" : "dropdown-closed"
        }`}
      >
        <a
          className="thinktravel-button"
          href="#"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            setDropdownOpen((prev) => !prev);
            if (onClick) {
              onClick();
            }
          }}
        >
          <span>{firstName ? `Hi, ${firstName}` : "Signing In"}</span>
        </a>
        <div className="dropdown-container">
          <div className="dropdown">
            <div className="section">
              <Link
                href="/account/reservations"
                onClick={() => setDropdownOpen(false)}
              >
                My reservations
              </Link>
              {/* <Link href="/account" onClick={() => setDropdownOpen(false)}>
                My account
              </Link> */}
            </div>
            <div className="section">
              <a
                href="#"
                onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                  e.preventDefault();
                  _signOut();
                }}
              >
                Sign Out
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="component auth-button">
      <a
        className="thinktravel-button"
        href="#"
        onClick={() => {
          showAuthenticator();
          if (onClick) {
            onClick();
          }
        }}
      >
        <span>Sign In</span>
      </a>
    </div>
  );
};

export default function AuthButtonWrapper({
  userAttributes,
  logoImage,
  onClick,
}: {
  userAttributes: FetchUserAttributesOutput | null;
  logoImage: ImageType;
  onClick?: () => void;
}) {
  const [showAuthenticator, setShowAuthenticator] = useState(false);
  const router = useRouter();

  return (
    <div className="component auth-button-wrapper">
      <Authenticator.Provider>
        <AuthStateChangeDetector
          action={() => {
            router.refresh();
            setShowAuthenticator(false);
          }}
        />
        <AuthButton
          showAuthenticator={() => setShowAuthenticator(true)}
          userAttributes={userAttributes}
          onClick={onClick}
        />
      </Authenticator.Provider>

      {/* The AuthForm needs to be outside the Provider or the create user form fails. */}
      {/* I think it's because of the nested Authenticator.Provider. */}
      {showAuthenticator && (
        <Modal>
          <AuthForm
            onClose={() => setShowAuthenticator(false)}
            logoImage={logoImage}
          />
        </Modal>
      )}
    </div>
  );
}
